"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { useEvents } from "../../../../availabilityDoctor/hooks/useEvents";
import useRendezvous from "../../../../appointments/hooks/useRendezvous";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { fetchRendezvousByMedecin } from "../../../../appointments/redux/thunks/rendezvousThunks";
import "./DoctorTodaySidePanel.css";
import doctorTodayCalendarOrangeIcon from "../../../../assets/icons/svg/doctor-today-calendar-orange.svg";

const pad2 = (n) => String(n).padStart(2, "0");

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const toDayKey = (date) => {
  const normalized = startOfDay(date);
  return `${normalized.getFullYear()}-${pad2(normalized.getMonth() + 1)}-${pad2(normalized.getDate())}`;
};

function buildMonthGrid(viewDate) {
  const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const month = d.getMonth();
  const firstDay = d.getDay();
  const startOffset = (firstDay + 6) % 7;
  const start = new Date(d);
  start.setDate(d.getDate() - startOffset);

  const days = [];
  for (let i = 0; i < 42; i++) {
    const x = new Date(start);
    x.setDate(start.getDate() + i);
    days.push({
      date: x,
      inMonth: x.getMonth() === month,
    });
  }
  return days;
}

const formatEventTime = (startTime) => {
  const date = new Date(startTime);
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  return `${hours}:${minutes}`;
};

/** ⛳ i18n helper: return "past / in X min / in Xh / HH:mm" */
const getTimeUntilEvent = (startTime, t) => {
  const now = new Date();
  const eventDate = new Date(startTime);
  const diffMs = eventDate - now;

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffMs < 0) return t("doctorToday.time.past");
  if (diffHours === 0 && diffMins < 60)
    return t("doctorToday.time.inMinutes", { minutes: diffMins });
  if (diffHours < 24) return t("doctorToday.time.inHours", { hours: diffHours });
  return formatEventTime(startTime);
};

const generateItemTitle = (item, type, t) => {
  const timeUntil = getTimeUntilEvent(item.startTime, t);

  const isMinutes =
    typeof timeUntil === "string" &&
    (timeUntil.includes("min") || timeUntil.includes("دقيقة"));

  if (type === "rendezvous") {
    if (isMinutes) return t("doctorToday.items.rendezvousSoon", { time: timeUntil });
    return t("doctorToday.items.rendezvous");
  }

  const baseTitle = item.title || t("doctorToday.items.event");
  if (isMinutes) return t("doctorToday.items.eventSoon", { title: baseTitle, time: timeUntil });
  return baseTitle;
};

export default function DoctorTodaySidePanel({ doctorId }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const isRTL = (i18n.language || "").startsWith("ar");
  const locale = isRTL ? "ar" : i18n.language || "en";

  const [viewDate, setViewDate] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));

  const { events, loading: eventsLoading } = useEvents({
    doctorIdOverride: doctorId,
    disableDoctorBootstrap: true,
  });
  const { getDoctorRendezvousByDate } = useRendezvous();

  const [doctorRendezvous, setDoctorRendezvous] = useState([]);
  const [monthRendezvous, setMonthRendezvous] = useState([]);
  const [rdvLoading, setRdvLoading] = useState(false);
  const [rdvError, setRdvError] = useState(null);
  const [patientNames, setPatientNames] = useState({});

  // ✅ Charger les rendez-vous du médecin
  useEffect(() => {
    if (!doctorId) return;

    const loadRendezvous = async () => {
      setRdvLoading(true);
      setRdvError(null);
      try {
        const rdvList = await getDoctorRendezvousByDate(
          doctorId,
          startOfDay(new Date())
        );
        setDoctorRendezvous(rdvList || []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur lors du chargement des rendez-vous:", error);
        setRdvError(error?.message || t("doctorToday.errors.unknown"));
        setDoctorRendezvous([]);
      } finally {
        setRdvLoading(false);
      }
    };

    loadRendezvous();
  }, [doctorId, getDoctorRendezvousByDate, t]);

  useEffect(() => {
    if (!doctorId) return;

    const loadMonthRendezvous = async () => {
      try {
        const raw = await dispatch(
          fetchRendezvousByMedecin({
            userId: doctorId,
            query: {},
          })
        ).unwrap();

        const month = viewDate.getMonth();
        const year = viewDate.getFullYear();

        const items = (Array.isArray(raw) ? raw : raw?.data || raw?.items || raw?.rendezvous || [])
          .filter((r) => {
            const status = String(r?.status || "").toLowerCase();
            const date = r?.startTime ? new Date(r.startTime) : null;
            return (
              date &&
              (status === "confirmed" || status === "rescheduled") &&
              date.getMonth() === month &&
              date.getFullYear() === year
            );
          });

        setMonthRendezvous(items);
      } catch (_) {
        setMonthRendezvous([]);
      }
    };

    loadMonthRendezvous();
  }, [dispatch, doctorId, viewDate]);

  // ✅ Charger les noms des patients
  useEffect(() => {
    const uniquePatientIds = [
      ...new Set(doctorRendezvous.map((rdv) => rdv.patientId).filter(Boolean)),
    ];

    if (uniquePatientIds.length === 0) return;

    const loadPatientNames = async () => {
      const names = {};
      await Promise.all(
        uniquePatientIds.map(async (patientId) => {
          try {
            const result = await dispatch(fetchUserById(patientId)).unwrap();
            const full =
              `${result.firstName || ""} ${result.lastName || ""}`.trim();
            names[patientId] = full || t("doctorToday.patientFallback");
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Erreur chargement patient ${patientId}:`, error);
            names[patientId] = t("doctorToday.patientFallback");
          }
        })
      );
      setPatientNames(names);
    };

    loadPatientNames();
  }, [doctorRendezvous, dispatch, t]);

  const monthLabel = useMemo(() => {
    const m = viewDate.toLocaleString(locale, { month: "long" });
    return `${m} ${viewDate.getFullYear()}`;
  }, [viewDate, locale]);

  const grid = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

  // ✅ Combiner events + rendezvous avec noms réels
  const todayItems = useMemo(() => {
    const today = startOfDay(new Date());
    const combined = [];

    // Events
    (events || [])
      .filter((event) => {
        if (!event?.startTime) return false;
        const eventDate = startOfDay(new Date(event.startTime));
        return sameDay(eventDate, today);
      })
      .forEach((event) => {
        combined.push({
          id: `event-${event.id || event._id}`,
          type: "event",
          title: generateItemTitle(event, "event", t),
          time: formatEventTime(event.startTime),
          startTime: event.startTime,
          location: event.location,
          source: "event",
        });
      });

    // Rendezvous avec nom du patient
    (doctorRendezvous || []).forEach((rdv) => {
      if (!rdv?.startTime) return;

      const patientDisplayName =
        patientNames[rdv.patientId] || rdv.patientName || t("doctorToday.patientFallback");

      combined.push({
        id: `rdv-${rdv.id || rdv._id || Math.random()}`,
        type: "rendezvous",
        title: generateItemTitle(rdv, "rendezvous", t),
        time: formatEventTime(rdv.startTime),
        startTime: rdv.startTime,
        patientName: patientDisplayName,
        source: "rendezvous",
      });
    });

    return combined.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [events, doctorRendezvous, patientNames, t]);

  const isLoading = eventsLoading || rdvLoading;
  const maxVisible = 3;
  const hasMore = todayItems.length > maxVisible;

  const goPrevMonth = () => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() - 1);
    setViewDate(startOfDay(d));
  };

  const goNextMonth = () => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() + 1);
    setViewDate(startOfDay(d));
  };

  const weekLabels = useMemo(
    () => {
      const labels = t("doctorToday.weekdays", { returnObjects: true });
      const fallback = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
      const base = Array.isArray(labels) ? labels : fallback;
      return [...base.slice(1), base[0]];
    },
    [t]
  );

  const eventDateKeys = useMemo(() => {
    const keys = new Set();

    (events || []).forEach((event) => {
      if (!event?.startTime) return;
      keys.add(toDayKey(new Date(event.startTime)));
    });

    (monthRendezvous || []).forEach((rdv) => {
      if (!rdv?.startTime) return;
      keys.add(toDayKey(new Date(rdv.startTime)));
    });

    return keys;
  }, [events, monthRendezvous]);

  return (
    <div className={`dts-wrap ${isRTL ? "is-rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <section className="dts-card">
        <div className="dts-head">
          <h3 className="dts-title">{t("doctorToday.title")}</h3>
          <div className="dts-countBadge">
            {todayItems.length} {t("doctorToday.items.event")}
          </div>
        </div>

        <div className={`dts-list ${hasMore ? "is-scroll" : ""}`}>
          {isLoading ? (
            <div className="dts-loading">{t("doctorToday.loading")}</div>
          ) : todayItems.length === 0 ? (
            <div className="dts-emptyRow">
              <span className="dts-emptyIcon" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M8.5 8.5L15.5 15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <span className="dts-emptyText">{t("doctorToday.empty")}</span>
            </div>
          ) : (
            todayItems.map((item) => (
              <div className="dts-item" key={item.id}>
                <div className="dts-icon" aria-hidden>
                  <img src={doctorTodayCalendarOrangeIcon} alt="" aria-hidden="true" width="16" height="16" />
                </div>

                <div className="dts-itemBody">
                  <div className="dts-itemTitle">
                    {item.title}
                    {item.source === "rendezvous" && item.patientName ? (
                      <span className="dts-patientInline">
                        {" "}
                        • {item.patientName}
                      </span>
                    ) : null}
                  </div>
                  <div className="dts-itemTime">{item.time}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {!isLoading && rdvError ? (
          <div className="dts-inlineNotice">
            {t("doctorToday.errors.errorPrefix")}: {rdvError}
          </div>
        ) : null}
      </section>

      <section className="dts-card dts-calCard">
        <div className="dts-calTop">
          <button
            className="dts-calArrow"
            onClick={goPrevMonth}
            type="button"
            aria-label={t("doctorToday.calendar.prevMonth")}
          >
            ‹
          </button>

          <div className="dts-calMonth">{monthLabel}</div>

          <button
            className="dts-calArrow"
            onClick={goNextMonth}
            type="button"
            aria-label={t("doctorToday.calendar.nextMonth")}
          >
            ›
          </button>
        </div>

        <div className="dts-week dts-week--figma">
          {(Array.isArray(weekLabels) ? weekLabels : ["SUN","MON","TUE","WED","THU","FRI","SAT"]).map((w) => (
            <div className="dts-weekCell dts-weekCell--figma" key={w}>
              {w}
            </div>
          ))}
        </div>

        <div className="dts-grid dts-grid--figma">
          {grid.map(({ date, inMonth }, i) => {
            const isSelected = sameDay(date, selectedDate);
            const isToday = sameDay(date, startOfDay(new Date()));
            const hasEvent = eventDateKeys.has(toDayKey(date));

            return (
              <button
                key={`${i}-${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`}
                className={[
                  "dts-day",
                  "dts-day--figma",
                  inMonth ? "is-inMonth" : "is-out",
                  hasEvent ? "has-event" : "",
                  isSelected ? "is-selected" : "",
                  isToday ? "is-today" : "",
                ].join(" ")}
                type="button"
                onClick={() => setSelectedDate(startOfDay(date))}
                title={date.toDateString()}
              >
                <span className="dts-dayNum">{date.getDate()}</span>
                {hasEvent ? <span className="dts-dayDot" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
